import { Flex, Skeleton } from '@chakra-ui/react';
import React from 'react';

import { BalanceSheetExposure } from './BalanceSheetExposure';
import { FacilitiesOverviewTiles } from './FacilitiesOverviewTiles';
import { FacilitiesTable } from './FacilitiesTable';
import { useFacilitiesTableData } from './useFacilitiesTableData';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { DefaultErrorRoute } from '~/core/DefaultErrorRoute';
import { PermissionGuard } from '~/core/PermissionGuard';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { UploadDrawerButton } from '~/packages/dataPipeline/ui/uploads/UploadDrawerButton';

// Fragments
const RouteBase = () => {
  const { user } = useUserAuthOrThrow();
  const {
    error,
    loading,
    computedRows,
    creditFacilities,
    totalsRow,
    hasBorrowingBase,
    balanceSheetImpactCalcs,
  } = useFacilitiesTableData();

  if (error) throw error;
  if (loading) return <Skeleton height="600px" />;

  return (
    <Flex direction="column" gap="4rem">
      <PermissionGuard
        permissions={(context) => {
          if (context.flags.isBorrowerLimitedOrganization) return 'deny';
          return 'allow';
        }}
      >
        {hasBorrowingBase && (
          <FacilitiesOverviewTiles
            creditFacilityCount={creditFacilities.length}
            totalsRow={totalsRow}
            rows={computedRows}
            balanceSheetImpactCalcs={balanceSheetImpactCalcs}
          />
        )}
        {hasBorrowingBase && (
          <BalanceSheetExposure
            balanceSheetImpactCalcs={balanceSheetImpactCalcs}
          />
        )}
      </PermissionGuard>
      <FacilitiesTable
        hasBorrowingBase={hasBorrowingBase}
        creditFacilities={creditFacilities}
        enableFacilitySelection={user.userType !== 'BORROWER'}
        rows={computedRows}
        totalsRow={totalsRow}
      />
    </Flex>
  );
};

// Exports
export const Route = () => {
  return (
    <RouteErrorBoundary
      // This is the "home" route for borrowers and cap providers, so we have no where to redirect
      // to on error and render a default error route instead
      resetAfterCatch={false}
      fallback={({ message, showErrorToast }) => {
        showErrorToast(message);
        return <DefaultErrorRoute />;
      }}
    >
      <Flex justifyContent="space-between" alignItems="center" mb="30px">
        <Typography variant="page-title" trim>
          Portfolio
        </Typography>
        <UploadDrawerButton buttonText="Generate Report" />
      </Flex>
      <RouteBase />
    </RouteErrorBoundary>
  );
};
